// src/pages/dashboard/components/KeyMetrics.jsx
import React, { useState, useEffect } from "react";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  ShoppingBag,
} from "lucide-react";
import { useLocalization } from "../../../contexts/LocalizationContext";
import { formatCurrency } from "../../../utils/currencyUtil";
import { getDashboardOverview } from "../../../services/api/salesReport";
import Popover from "../../../components/common/Popover/Popover";
import styles from "./KeyMetrics.module.css";

const KeyMetrics = ({ shopId, currency }) => {
  const { t } = useLocalization();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, [shopId]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (shopId && shopId !== "all") {
        params.shopId = shopId;
      }
      const response = await getDashboardOverview(params);
      setData(response);
    } catch (err) {
      console.error("Error fetching dashboard overview:", err);
      setError(err.message || t("dashboard.errorLoading"));
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat("en-US").format(value || 0);
  };

  const renderChangeIndicator = (percentageChange) => {
    const isPositive = percentageChange >= 0;
    const Icon = isPositive ? TrendingUp : TrendingDown;

    return (
      <div
        className={`${styles.change} ${
          isPositive ? styles.positive : styles.negative
        }`}
      >
        <Icon size={14} />
        <span>{Math.abs(percentageChange).toFixed(1)}%</span>
      </div>
    );
  };

  const renderComparisonRow = (label, current, previous, valueChange, orderChange) => (
    <div className={styles.comparisonRow}>
      <div className={styles.comparisonTitle}>{label}</div>
      <div className={styles.comparisonLine}>
        <span>{t("dashboard.salesValue")}</span>
        <span>
          {formatCurrency(current?.salesValue || 0, currency)} vs{" "}
          {formatCurrency(previous?.salesValue || 0, currency)}
        </span>
        {renderChangeIndicator(valueChange || 0)}
      </div>
      <div className={styles.comparisonLine}>
        <span>{t("dashboard.orders")}</span>
        <span>
          {formatNumber(current?.orderCount || 0)} vs{" "}
          {formatNumber(previous?.orderCount || 0)}
        </span>
        {renderChangeIndicator(orderChange || 0)}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className={styles.metricsGrid}>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className={styles.metricCard}>
            <div className={styles.skeleton}>
              <div className={styles.skeletonHeader}></div>
              <div className={styles.skeletonValue}></div>
              <div className={styles.skeletonChange}></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <p className={styles.errorMessage}>{error}</p>
        <button onClick={fetchDashboardData} className={styles.retryButton}>
          {t("dashboard.tryAgain")}
        </button>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const metrics = [
    {
      title: t("dashboard.todaysSales"),
      value: formatCurrency(data.today?.salesValue || 0, currency),
      subValue: `${formatNumber(data.today?.orderCount || 0)} ${t(
        "dashboard.orders"
      )}`,
      change: data.today?.percentageChange,
      icon: DollarSign,
      color: "#10b981",
      showChange: true,
      changeLabel: t("dashboard.vsYesterday"),
      changePopover: (
        <div className={styles.comparisonPopover}>
          {renderComparisonRow(
            t("dashboard.todayVsYesterday"),
            data.today,
            data.today?.previous,
            data.today?.percentageChange,
            data.today?.orderPercentageChange
          )}
        </div>
      ),
    },
    {
      title: t("dashboard.thisWeek"),
      value: formatCurrency(data.week?.current?.salesValue || 0, currency),
      subValue: `${formatNumber(data.week?.current?.orderCount || 0)} ${t(
        "dashboard.orders"
      )}`,
      change: data.week?.percentageChange,
      icon: TrendingUp,
      color: "#7F5CFF",
      showChange: true,
      changeLabel: t("dashboard.vsLastWeekSameDays"),
      changePopover: (
        <div className={styles.comparisonPopover}>
          {renderComparisonRow(
            t("dashboard.weekToDateVsLastWeek"),
            data.week?.current,
            data.week?.previous,
            data.week?.percentageChange,
            data.week?.orderPercentageChange
          )}
          {renderComparisonRow(
            t("dashboard.weekToDateVsFullLastWeek"),
            data.week?.current,
            data.week?.fullPrevious,
            data.week?.fullPercentageChange,
            data.week?.fullOrderPercentageChange
          )}
        </div>
      ),
    },
    {
      title: t("dashboard.thisMonth"),
      value: formatCurrency(data.month?.current?.salesValue || 0, currency),
      subValue: `${formatNumber(data.month?.current?.orderCount || 0)} ${t(
        "dashboard.orders"
      )}`,
      change: data.month?.percentageChange,
      icon: ShoppingBag,
      color: "#f59e0b",
      showChange: true,
      changeLabel: t("dashboard.vsLastMonthSameDays"),
      changePopover: (
        <div className={styles.comparisonPopover}>
          {renderComparisonRow(
            t("dashboard.monthToDateVsLastMonth"),
            data.month?.current,
            data.month?.previous,
            data.month?.percentageChange,
            data.month?.orderPercentageChange
          )}
          {renderComparisonRow(
            t("dashboard.monthToDateVsFullLastMonth"),
            data.month?.current,
            data.month?.fullPrevious,
            data.month?.fullPercentageChange,
            data.month?.fullOrderPercentageChange
          )}
        </div>
      ),
    },
    {
      title: t("dashboard.avgOrderValue"),
      value: formatCurrency(data.averageOrderValue || 0, currency),
      subValue: t("dashboard.thisMonth"),
      icon: DollarSign,
      color: "#8b5cf6",
    },
  ];

  return (
    <div className={styles.metricsGrid}>
      {metrics.map((metric, index) => {
        const Icon = metric.icon;
        return (
          <div key={index} className={styles.metricCard}>
            <div className={styles.cardHeader}>
              <div className={styles.title}>{metric.title}</div>
              <div
                className={styles.icon}
                style={{
                  backgroundColor: `${metric.color}15`,
                  color: metric.color,
                }}
              >
                <Icon size={20} />
              </div>
            </div>
            <div className={styles.cardContent}>
              <div className={styles.value}>{metric.value}</div>
              <div className={styles.subInfo}>
                <span className={styles.subValue}>{metric.subValue}</span>
                {metric.showChange && metric.change !== undefined && (
                  <>
                    {metric.changePopover ? (
                      <Popover trigger={renderChangeIndicator(metric.change)} align="start">
                        {metric.changePopover}
                      </Popover>
                    ) : (
                      renderChangeIndicator(metric.change)
                    )}
                    <span className={styles.changeLabel}>
                      {metric.changeLabel}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default KeyMetrics;
